import "./SettingsBlock.scss"
import IconPencil from "assets/img/icon-pencil.svg?react"
import IconArrowRight from "assets/img/icon-arrow-right.svg?react"
import IconBook from "assets/img/icon-book.svg?react"

import { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import cx from "classnames"

import { Flex, FlexItem } from "components/Flex"
import Button from "components/Button/Button"
import Spoiler from "components/Spoiler/Spoiler"

interface ISettingsBlockProps {
    title?: ReactNode
    expandableId?: string
    expanded?: boolean
    wrapperClass?: string
    toolbar?: ReactNode
    onToggle?(expanded: boolean, sectionId: string): void
    onInfoClick?(): void
    onEditClick?(): void
    onViewClick?(): void
    testId?: string
}

const SettingsBlock: FC<React.PropsWithChildren<ISettingsBlockProps>> = ({
    title,
    expandableId,
    expanded,
    wrapperClass = "p-4 bg-white round",
    toolbar,
    onToggle,
    onInfoClick,
    onEditClick,
    onViewClick,
    testId,
    children,
}) => {
    const { t } = useTranslation()
    const isExpandable = !!expandableId && !!onToggle

    function renderHeaderTitle(isExpanded?: boolean, toggle?: () => void) {
        if (isExpandable) {
            return (
                <button type="button" className={cx([
                        "settings-block__toggle-button",
                        { "settings-block__toggle-button--expanded": isExpanded },
                    ])}
                    data-testid="toggle-btn"
                    onClick={toggle}>
                    <div className="settings-block__arrow"></div>
                    {title}
                </button>
            )
        }
        return title
    }

    const headerControls = (
        <FlexItem shrink={0}>
            <Flex alignItems="center">
                {!!toolbar &&
                    <FlexItem shrink={0}>
                        {toolbar}
                    </FlexItem>
                }
                {!!onInfoClick &&
                    <FlexItem>
                        <Button
                            className="ml-4"
                            theme="link"
                            icon={<IconBook />}
                            label={t('common.info')}
                            data-testid="info-btn"
                            onClick={onInfoClick}
                        />
                    </FlexItem>
                }
                {!!onEditClick &&
                    <FlexItem>
                        <Button
                            className="ml-4"
                            round
                            theme="grey"
                            icon={<IconPencil />}
                            ariaLabel={t("common.edit")}
                            data-testid="edit-btn"
                            onClick={onEditClick}
                        />
                    </FlexItem>
                }
                {!!onViewClick &&
                    <FlexItem>
                        <Button
                            className="ml-4"
                            round
                            theme="grey"
                            icon={<IconArrowRight />}
                            data-testid="view-btn"
                            onClick={onViewClick}
                        />
                    </FlexItem>
                }
            </Flex>
        </FlexItem>
    )

    function renderHeader(isExpanded?: boolean, toggle?: () => void) {
        return (
            <Flex
                alignItems="center"
                justifyContent="space-between">
                {renderHeaderTitle(isExpanded, toggle)}
                {headerControls}
            </Flex>
        )
    }

    const content = (
        <>
            {!!children &&
                <div className={cx([{ "mt-2": !!title, "pl-4": isExpandable }])}>
                    {children}
                </div>
            }
        </>
    )

    return (
        <div className={wrapperClass} data-testid={testId}>
            {isExpandable && !!onToggle ?
                <Spoiler
                    id={expandableId}
                    expanded={expanded}
                    onToggle={onToggle}
                    toggleEl={(isExpanded, toggle) => renderHeader(isExpanded, toggle)}>
                    {content}
                </Spoiler>
            :
                <>
                    {renderHeader()}
                    {content}
                </>
            }
        </div>
    )
}

export default SettingsBlock
